import { useState, useEffect } from 'react';
import { api } from '../../../services/api';

interface HabitType {
  id: string;
  nom: string;
  prix: number;
  icon: string;
}

interface SelectedItem {
    type_habit_id: string;
    nom: string;
    prix_unitaire: number;
    quantite: number;
}

interface Props {
  selectedItems: SelectedItem[];
  onChange: (items: SelectedItem[]) => void;
}

export default function StepTypeCouture({ selectedItems, onChange }: Props) {
  const [types, setTypes] = useState<HabitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchTypes = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (token) {
        try {
          const data = await api.getHabitTypes(token);
          setTypes(data);
        } catch (e) {
          console.error("Erreur chargement types habit", e);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchTypes();
  }, []);

  const updateQty = (type: HabitType, delta: number) => {
    const existing = selectedItems.find(i => i.type_habit_id === type.id);
    let newItems = [...selectedItems];

    if (existing) {
      const newQty = existing.quantite + delta;
      if (newQty <= 0) {
        newItems = newItems.filter(i => i.type_habit_id !== type.id);
      } else {
        newItems = newItems.map(i => i.type_habit_id === type.id ? { ...i, quantite: newQty } : i);
      }
    } else if (delta > 0) {
      newItems.push({
        type_habit_id: type.id,
        nom: type.nom,
        prix_unitaire: Number(type.prix) || 0,
        quantite: 1
      });
    }
    onChange(newItems);
  };

  const filteredTypes = types.filter(t => t.nom.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return <div className="p-8 text-center text-gray-500">Chargement des types de couture...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-xl border border-gray-100">
        <i className="ri-search-line text-gray-400 ml-2" />
        <input 
          type="text" 
          placeholder="Rechercher un type (Boubou, Pantalon...)" 
          className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-2"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {filteredTypes.map(t => {
          const selected = selectedItems.find(si => si.type_habit_id === t.id);
          return (
            <div key={t.id} className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer group ${
              selected ? 'border-foret-500 bg-foret-50' : 'border-gray-100 hover:border-gray-200 bg-white'
            }`}>
              <div className="flex flex-col items-center text-center gap-2">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                    selected ? 'bg-foret-500 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  <i className={t.icon || 'ri-t-shirt-line'} />
                </div>
                <h4 className="font-bold text-sm text-gray-800 line-clamp-1">{t.nom}</h4>
                <p className="text-xs font-bold text-foret-600">{Number(t.prix).toLocaleString()} FCFA</p>
              </div>

              {selected ? (
                <div className="mt-4 flex items-center justify-center gap-3">
                  <button 
                    onClick={() => updateQty(t, -1)} 
                    className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 text-gray-600"
                    title="Diminuer la quantité"
                    aria-label="Diminuer la quantité"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <span className="font-bold text-sm text-gray-900">{selected.quantite}</span>
                  <button 
                    onClick={() => updateQty(t, 1)} 
                    className="w-8 h-8 rounded-lg bg-foret-500 text-white flex items-center justify-center hover:bg-foret-600"
                    title="Augmenter la quantité"
                    aria-label="Augmenter la quantité"
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => updateQty(t, 1)}
                  className="mt-4 w-full py-2 rounded-lg border border-gray-200 text-xs font-bold text-gray-500 group-hover:bg-foret-500 group-hover:text-white group-hover:border-foret-500 transition-colors"
                >
                  Ajouter
                </button>
              )}
            </div>
          );
        })}
      </div>

      {selectedItems.length > 0 && (
          <div className="bg-foret-900 text-white p-4 rounded-2xl shadow-lg flex items-center justify-between">
              <div>
                  <p className="text-[10px] uppercase font-bold text-foret-300">Sélection</p>
                  <p className="text-sm font-bold">{selectedItems.length} type(s) sélectionné(s)</p>
              </div>
              <div className="text-right">
                  <p className="text-[10px] uppercase font-bold text-foret-300">Total estimé</p>
                  <p className="text-xl font-playfair font-bold">
                      {selectedItems.reduce((acc, i) => acc + (i.prix_unitaire * i.quantite), 0).toLocaleString()} <span className="text-xs">FCFA</span>
                  </p>
              </div>
          </div>
      )}

      {filteredTypes.length === 0 && (
          <div className="p-8 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Aucun type trouvé pour "{searchTerm}"</p>
              <button className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100">
                  <i className="ri-add-line mr-2" />
                  Créer un nouveau type personnalisé
              </button>
          </div>
      )}
    </div>
  );
}
